import { useRef, useState } from "react";
import { ReferenceImagePayload } from "./types";

const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;

function readFileAsBase64(file: File): Promise<ReferenceImagePayload> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const [header, base64] = dataUrl.split(",", 2);
      const mimeMatch = header?.match(/^data:(.*);base64$/);
      if (!base64 || !mimeMatch) {
        reject(new Error("Failed to read the uploaded image"));
        return;
      }
      resolve({ mimeType: mimeMatch[1], base64 });
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read the uploaded image"));
    reader.readAsDataURL(file);
  });
}

export interface ReferenceImageField {
  referenceImage: ReferenceImagePayload | null;
  previewUrl: string | null;
  fileName: string;
  isConverting: boolean;
  error: string;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  accept: string;
  openPicker: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  remove: () => void;
}

export function useReferenceImage(allowedMimeTypes: readonly string[], maxBytes: number = DEFAULT_MAX_BYTES): ReferenceImageField {
  const allowed = new Set(allowedMimeTypes);
  const [referenceImage, setReferenceImage] = useState<ReferenceImagePayload | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const clearImage = () => {
    setReferenceImage(null);
    setFileName("");
    setPreviewUrl(null);
  };

  const remove = () => {
    clearImage();
    setError("");
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsConverting(true);
    setError("");

    try {
      if (!allowed.has(file.type)) {
        throw new Error(`Unsupported image type: ${file.type}. Allowed types: ${[...allowed].join(", ")}`);
      }
      if (file.size > maxBytes) {
        throw new Error(`Image is too large (${file.size} bytes). Maximum allowed is ${maxBytes} bytes.`);
      }
      const payload = await readFileAsBase64(file);
      setReferenceImage(payload);
      setFileName(file.name);
      setPreviewUrl(`data:${payload.mimeType};base64,${payload.base64}`);
    } catch (err) {
      clearImage();
      setError(err instanceof Error ? err.message : "Failed to read the uploaded image");
    } finally {
      setIsConverting(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return {
    referenceImage,
    previewUrl,
    fileName,
    isConverting,
    error,
    inputRef,
    accept: allowedMimeTypes.join(","),
    openPicker: () => inputRef.current?.click(),
    onFileChange,
    remove,
  };
}
